import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, Navigation, MessageCircle, Package, Calculator, FileText, CheckCircle, Phone, Mail, MapPin } from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

const QuotationViewer = () => {
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const token = window.location.pathname.split('/q/')[1];
  const toNumber = (v) => v == null ? 0 : Number(v);

  useEffect(() => {
    loadQuotation();
  }, []);

  const loadQuotation = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/q/${token}`);
      console.log('Raw API response:', res.data);

      const rawData = res.data.success ? res.data.data : res.data;

      // Calculate unit_price * quantity for each item (excluding GST)
      const parsedItems = rawData.items?.map(item => {
        const qty = toNumber(item.quantity);
        const price = toNumber(item.unit_price);
        const lineTotal = price * qty;
        return {
          ...item,
          quantity: qty,
          unit_price: price,
          line_total: lineTotal,
          total_price: lineTotal
        };
      }) || [];

      // Calculate subtotal as sum of line totals
      const subtotal = parsedItems.reduce((acc, item) => acc + item.line_total, 0);
      const charges = toNumber(rawData.charges);
      const discount = toNumber(rawData.discount);
      const grandTotal = subtotal + charges - discount;

      const parsed = {
        ...rawData,
        subtotal: subtotal,
        charges: charges,
        discount: discount,
        gst_amount: 0, // GST removed
        total: grandTotal,
        items: parsedItems
      };

      console.log('Parsed quotation (No GST):', parsed);
      setQuotation(parsed);
    } catch (err) {
      console.error("Failed to load quotation:", err);
      setError(err.response?.data?.message || "Failed to load quotation");
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      setDownloading(true);

      // Use browser's print functionality with custom CSS
      const printWindow = window.open('', '', 'width=800,height=600');
      printWindow.document.write(generatePrintHTML());

      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 300));
      printWindow.document.close();
    } catch (err) {
      console.error("Failed to generate PDF:", err);
      alert("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const generatePrintHTML = () => {
    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };

    const currency = (amount) => {
      return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const roundOff = Math.round(quotation.total) - quotation.total;

    const summaryItems = [
      { label: 'Subtotal', value: quotation.subtotal },
      ...(quotation.charges > 0 ? [{ label: 'Additional Charges', value: quotation.charges }] : []),
      ...(quotation.discount > 0 ? [{ label: 'Discount', value: -quotation.discount, negative: true }] : []),
      ...(Math.abs(roundOff) > 0.01 ? [{ label: 'Round Off', value: roundOff }] : [])
    ];

    const ITEMS_PER_PAGE = 18;
    const totalPages = Math.ceil(quotation.items.length / ITEMS_PER_PAGE);

    const pages = [];
    for (let i = 0; i < totalPages; i++) {
      const startIdx = i * ITEMS_PER_PAGE;
      const endIdx = Math.min(startIdx + ITEMS_PER_PAGE, quotation.items.length);
      pages.push(quotation.items.slice(startIdx, endIdx));
    }

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Quotation - ${quotation.quote_no}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    @page {
      size: A4;
      margin: 12mm;
    }
    
    @media print {
      body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
    
    body {
      font-family: Arial, sans-serif;
      font-size: 9pt;
      line-height: 1.3;
      color: #000;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .page {
      page-break-after: always;
      position: relative;
    }
    
    .page:last-child {
      page-break-after: auto;
    }
    
    /* Header */
    .header {
      background: linear-gradient(135deg, #0891b2 0%, #1e40af 100%);
      padding: 14px 16px;
      margin-bottom: 12px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }
    
    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
    }
    
    .logo {
      width: 65px;
      height: 65px;
      background: white;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .company-info {
      flex: 1;
    }
    
    .company-info h1 {
      color: white;
      font-size: 15pt;
      font-weight: bold;
      margin-bottom: 6px;
    }
    
    .company-details {
      color: white;
      font-size: 7pt;
      line-height: 1.6;
    }
    
    .company-details p {
      margin-bottom: 2px;
      display: flex;
      align-items: center;
      gap: 4px;
    }
    
    .company-details svg {
      width: 10px;
      height: 10px;
      fill: white;
      flex-shrink: 0;
    }
    
    .quote-info-right {
      background: rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      padding: 8px 12px;
      min-width: 140px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .quote-info-item {
      margin-bottom: 8px;
    }
    
    .quote-info-item:last-child {
      margin-bottom: 0;
    }
    
    .quote-info-label {
      color: #bfdbfe;
      font-size: 6.5pt;
      margin-bottom: 2px;
    }
    
    .quote-info-value {
      color: white;
      font-weight: bold;
      font-size: 9pt;
    }
    
    /* Items Table */
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 0;
    }
    
    .items-table thead {
      background: #0891b2;
      color: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .items-table th {
      padding: 8px 6px;
      text-align: left;
      font-size: 8pt;
      font-weight: bold;
    }
    
    .items-table td {
      padding: 6px;
      border-bottom: 1px solid #e5e7eb;
      font-size: 8pt;
    }
    
    .items-table .text-right {
      text-align: right;
    }
    
    .items-table .text-center {
      text-align: center;
    }
    
    /* Summary Section */
    .bottom-section {
      display: flex;
      justify-content: flex-end;
      margin-top: 8px;
      page-break-inside: avoid;
    }
    
    /* Summary */
    .summary {
      border: 1px solid #d1d5db;
      border-radius: 4px;
      padding: 6px 8px;
      width: 240px;
    }
    
    .summary h4 {
      font-size: 7.5pt;
      font-weight: bold;
      margin-bottom: 5px;
      text-transform: uppercase;
    }
    
    .summary-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 2px;
      font-size: 6.5pt;
    }
    
    .summary-row .label {
      color: #374151;
    }
    
    .summary-row .value {
      font-weight: 600;
      color: #000;
    }
    
    .summary-row.total {
      border-top: 2px solid #000;
      padding-top: 4px;
      margin-top: 4px;
    }
    
    .summary-row.total .label {
      font-weight: bold;
      font-size: 7.5pt;
    }
    
    .summary-row.total .value {
      font-weight: bold;
      font-size: 8.5pt;
    }
    
    .summary .note {
      font-size: 5.5pt;
      color: #666;
      margin-top: 2px;
      text-align: right;
    }
    
    /* Footer */
    .footer {
      text-align: center;
      font-size: 7pt;
      color: #666;
      padding-top: 8px;
      margin-top: 10px;
      border-top: 1px solid #e5e7eb;
    }
    
    .footer p {
      margin-bottom: 2px;
    }
  </style>
</head>
<body>
  ${pages.map((pageItems, pageIndex) => `
    <div class="page">
      <!-- Header - Only on first page -->
      ${pageIndex === 0 ? `
        <div class="header">
          <div class="header-content">
            <div class="header-left">
              <div class="logo">
                <img 
                    src="/logo.png" 
                    alt="KTZ Logo" 
                    style="width: 50px; height: 50px; object-fit: contain;"
                  />
              </div>
              <div class="company-info">
                <h1>KWALITY TECH ZONE LLP</h1>
                <div class="company-details">
                  <p>
                    <svg viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                    <span>No.116/1A, Muniswamappa Lane, S.P. Road Cross, Bangalore - 560002</span>
                  </p>
                  <p>
                    <svg viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
                    <span>9739933180 (WhatsApp) | 9739733180</span>
                  </p>
                  <p>
                    <svg viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                    <span>kwalitytechzone@gmail.com</span>
                  </p>
                  <p>
                    <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
                    <span>GST: 29ABDFK3857D1Z8</span>
                  </p>
                </div>
              </div>
            </div>
            <div class="quote-info-right">
              <div class="quote-info-item">
                <p class="quote-info-label">Date</p>
                <p class="quote-info-value">${formatDate(quotation.created_at)}</p>
              </div>
              <div class="quote-info-item">
                <p class="quote-info-label">Quotation No.</p>
                <p class="quote-info-value">${quotation.quote_no}</p>
              </div>
            </div>
          </div>
        </div>
      ` : ''}
      
      <!-- Items Table -->
      <table class="items-table">
        <thead>
          <tr>
            <th style="width: 40px;">S.No</th>
            <th>Product Name</th>
            <th style="width: 50px;" class="text-center">Qty</th>
            <th style="width: 85px;" class="text-right">Unit Price</th>
            <th style="width: 90px;" class="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          ${pageItems.map((item, index) => `
            <tr>
              <td class="text-center">${pageIndex * ITEMS_PER_PAGE + index + 1}</td>
              <td>${item.product_name || item.name}</td>
              <td class="text-center">${item.quantity}</td>
              <td class="text-right">${currency(item.unit_price)}</td>
              <td class="text-right"><strong>${currency(item.line_total)}</strong></td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <!-- Summary - Only on last page -->
      ${pageIndex === pages.length - 1 ? `
        <div class="bottom-section">
          <!-- Summary -->
          <div class="summary">
            <h4>Summary</h4>
            ${summaryItems.map(item => `
              <div class="summary-row">
                <span class="label">${item.label}</span>
                <span class="value">${item.negative ? '-' : ''}${currency(Math.abs(item.value))}</span>
              </div>
            `).join('')}
            <div class="summary-row total">
              <span class="label">GRAND TOTAL</span>
              <span class="value">₹${Math.round(quotation.total).toLocaleString('en-IN')}</span>
            </div>
            <p class="note">(Rounded to nearest rupee)</p>
          </div>
        </div>
      ` : ''}
      
      <!-- Footer -->
      <div class="footer">
        <p><strong>KWALITY TECH ZONE LLP</strong> - Your Trusted Partner for Quality Tech Products</p>
        <p>For queries, contact us: 9739933180 | kwalitytechzone@gmail.com</p>
      </div>
    </div>
  `).join('')}
  
  <script>
    window.addEventListener('load', function() {
      setTimeout(() => {
        window.print();
      }, 200);
    });
  </script>
</body>
</html>
    `;
  };

  const openWhatsApp = () => {
    const amount = Math.round(quotation.total);
    const message = `Hi, I have a query regarding Quotation ${quotation.quote_no} (Total: ₹${amount})`;
    window.open(`https://wa.me/919739933180?text=${encodeURIComponent(message)}`, '_blank');
  };

  const openGoogleMaps = () => {
    window.open(`https://maps.app.goo.gl/MWh9BkyWAzrnnVup7`, '_blank');
  };

  const roundOff = quotation ? (Math.round(quotation.total) - quotation.total) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading quotation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Quotation Not Found</h2>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 py-4 md:py-8 px-3 md:px-4">
      <div className="max-w-5xl mx-auto">
        {/* Action Buttons */}
        <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-3 mb-4 md:mb-6">
          <button
            onClick={downloadPDF}
            disabled={downloading}
            className="flex items-center justify-center gap-2 bg-cyan-600 text-white px-3 md:px-6 py-2 md:py-3 rounded-lg hover:bg-cyan-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md text-sm md:text-base"
          >
            <Download className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">{downloading ? "Downloading..." : "Download PDF"}</span>
            <span className="sm:hidden">PDF</span>
          </button>
          <button
            onClick={openWhatsApp}
            className="flex items-center justify-center gap-2 bg-green-600 text-white px-3 md:px-6 py-2 md:py-3 rounded-lg hover:bg-green-700 transition-colors shadow-md text-sm md:text-base"
          >
            <MessageCircle className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">WhatsApp</span>
            <span className="sm:hidden">Chat</span>
          </button>
          <button
            onClick={openGoogleMaps}
            className="flex items-center justify-center gap-2 bg-blue-600 text-white px-3 md:px-6 py-2 md:py-3 rounded-lg hover:bg-blue-700 transition-colors shadow-md text-sm md:text-base"
          >
            <Navigation className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Directions</span>
            <span className="sm:hidden">Map</span>
          </button>
        </div>

        {/* Quotation Content */}
        <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-600 to-blue-700 text-white p-4 md:p-8">
            <div className="flex flex-col items-center md:flex-row md:items-start md:justify-between gap-4 md:gap-6 mb-4">
              <div className="flex flex-col items-center md:items-start gap-3 md:flex-row md:gap-4">
                <div className="bg-white rounded-lg p-2 shadow-lg">
                  <img
                    src="/logo.png"
                    alt="KTZ Logo"
                    className="w-16 h-16 md:w-24 md:h-24 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div className="text-center md:text-left">
                  <h1 className="text-xl md:text-3xl font-bold mb-1 md:mb-2">KWALITY TECH ZONE LLP</h1>
                  <div className="space-y-2 text-xs md:text-sm text-cyan-100 mb-4">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>No.116/1A, Muniswamappa Lane, S.P. Road Cross, Bangalore - 560002</span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>9739933180 (WhatsApp) | 9739733180</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <span>kwalitytechzone@gmail.com</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      <span className="font-semibold">GST: 29ABDFK3857D1Z8</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quotation Number & Date */}
            <div className="grid grid-cols-2 gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 md:p-4">
              <div>
                <p className="text-xs text-cyan-200 mb-1">Quotation No.</p>
                <p className="font-bold text-sm md:text-lg">{quotation.quote_no}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-cyan-200 mb-1">Date</p>
                <p className="font-bold text-sm md:text-lg">
                  {new Date(quotation.created_at).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-4 md:p-8">
            {/* Items */}
            <div className="mb-6 md:mb-8">
              <h2 className="text-lg md:text-xl font-bold text-slate-800 mb-3 md:mb-4 flex items-center gap-2 border-b-2 border-slate-300 pb-2">
                <Package className="w-4 h-4 md:w-5 md:h-5 text-cyan-600" />
                ITEM DETAILS
              </h2>

              {/* Mobile Card View */}
              <div className="block md:hidden space-y-3">
                {quotation.items.map((item, index) => (
                  <div key={index} className="bg-slate-50 border border-slate-200 rounded-lg p-4 shadow-sm">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-start gap-2">
                          <span className="bg-cyan-600 text-white text-xs font-bold px-2 py-1 rounded">{index + 1}</span>
                          <h3 className="font-bold text-slate-900 text-sm leading-tight">{item.product_name}</h3>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div>
                        <p className="text-xs text-slate-500">Quantity</p>
                        <p className="font-semibold text-slate-800">{item.quantity}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Unit Price</p>
                        <p className="font-semibold text-slate-800">₹{item.unit_price.toFixed(2)}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-slate-500">Total</p>
                        <p className="font-bold text-cyan-600 text-base">₹{item.line_total.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto border border-slate-300 rounded">
                <table className="w-full">
                  <thead className="bg-cyan-600">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-white border-b border-cyan-700">S.No</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-white border-b border-cyan-700">Product Name</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-white border-b border-cyan-700">Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-white border-b border-cyan-700">Unit Price</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-white border-b border-cyan-700">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {quotation.items.map((item, index) => (
                      <tr key={index} className={`border-b border-slate-200 ${index % 2 === 0 ? 'bg-slate-50' : ''}`}>
                        <td className="px-4 py-3 text-sm text-slate-700">{index + 1}</td>
                        <td className="px-4 py-3 text-sm text-slate-900 font-medium">{item.product_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-700 text-center">{item.quantity}</td>
                        <td className="px-4 py-3 text-sm text-slate-700 text-right">₹{item.unit_price.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-slate-900 font-semibold text-right">₹{item.line_total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary */}
            <div className="mb-6 md:mb-8">
              <h3 className="text-lg md:text-xl font-bold text-slate-800 mb-3 md:mb-4 flex items-center gap-2 border-b-2 border-slate-300 pb-2">
                <Calculator className="w-4 h-4 md:w-5 md:h-5 text-cyan-600" />
                SUMMARY
              </h3>

              {/* Mobile Layout */}
              <div className="md:hidden">
                <div className="bg-slate-50 rounded-lg border-2 border-slate-300 p-4 shadow-md">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-600">Subtotal</span>
                      <span className="font-semibold text-slate-800">₹{quotation.subtotal.toFixed(2)}</span>
                    </div>
                    {quotation.charges > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-600">Additional Charges</span>
                        <span className="font-semibold text-slate-800">₹{quotation.charges.toFixed(2)}</span>
                      </div>
                    )}
                    {quotation.discount > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-600">Discount</span>
                        <span className="font-semibold text-green-600">-₹{quotation.discount.toFixed(2)}</span>
                      </div>
                    )}
                    {Math.abs(roundOff) > 0.01 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-600">Round Off</span>
                        <span className="font-semibold text-slate-800">
                          {roundOff > 0 ? '+' : ''}₹{roundOff.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="border-t-2 border-cyan-600 pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-bold text-slate-800">GRAND TOTAL</span>
                        <span className="text-xl font-bold text-cyan-600">₹{Math.round(quotation.total)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Layout */}
              <div className="hidden md:block">
                <div className="flex justify-end">
                  <div className="bg-slate-50 rounded-lg border-2 border-slate-300 p-6 shadow-md w-full max-w-md">
                    <div className="space-y-2.5">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-600">Subtotal</span>
                        <span className="font-semibold text-slate-800">₹{quotation.subtotal.toFixed(2)}</span>
                      </div>
                      {quotation.charges > 0 && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-slate-600">Additional Charges</span>
                          <span className="font-semibold text-slate-800">₹{quotation.charges.toFixed(2)}</span>
                        </div>
                      )}
                      {quotation.discount > 0 && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-slate-600">Discount</span>
                          <span className="font-semibold text-green-600">-₹{quotation.discount.toFixed(2)}</span>
                        </div>
                      )}
                      {Math.abs(roundOff) > 0.01 && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-slate-600">Round Off</span>
                          <span className="font-semibold text-slate-800">
                            {roundOff > 0 ? '+' : ''}₹{roundOff.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="border-t-2 border-cyan-600 pt-3 mt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-bold text-slate-800">GRAND TOTAL</span>
                          <span className="text-2xl font-bold text-cyan-600">₹{Math.round(quotation.total)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-slate-800 text-white py-4 md:py-6 px-4 md:px-8 text-center">
            <h4 className="font-bold text-base md:text-lg mb-1">KWALITY TECH ZONE LLP</h4>
            <p className="text-slate-300 text-xs md:text-sm mb-2">Premium Electronics & CCTV Solutions</p>
            <p className="text-slate-400 text-xs">
              No.116/1A, Muniswamappa Lane, S.P. Road Cross, Bangalore, Karnataka - 560002
            </p>
            <p className="text-slate-400 text-xs mt-1">
              Phone: 9739933180 | 9739733180 | Email: kwalitytechzone@gmail.com
            </p>
            <p className="text-slate-400 text-xs mt-1">
              NOTE: THIS IS NOT AN ACTUAL INVOICE. This represents only an estimate and should not be considered as any actual invoice.
              This Link is Under Development and should only be considered for testing purpose.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationViewer;
